/**
 * channel-access.ts — Nhãn / mô tả dùng chung cho hai khái niệm (xem docs/PHAN-BO-HOI-THOAI.md):
 *
 *   Mức quyền của NHÂN VIÊN trên một kênh   : Quản lý kênh | Tiếp nhận
 *   Chế độ nhận hội thoại của MỘT kênh      : Chia tự động | Hàng chờ chung | Chỉ quản lý giao
 *
 * Khớp backend `shared/reception-mode.ts`. Mọi chữ hiện ra giao diện về hai khái niệm này lấy ở đây để không lệch nhau.
 */
import { i18n } from '@/lib/i18n'

export type AccessLevel = 'manage' | 'agent'
export type ReceptionMode = 'auto' | 'queue' | 'manual'

export const ACCESS_LEVELS: AccessLevel[] = ['manage', 'agent']
export const RECEPTION_MODES: ReceptionMode[] = ['auto', 'queue', 'manual']

/** Mức mặc định khi giao kênh MỚI cho nhân viên: Tiếp nhận (chỉ thấy phần được giao) — an toàn về dữ liệu. */
export const DEFAULT_ACCESS_LEVEL: AccessLevel = 'agent'

export const ACCESS_LEVEL_META: Record<AccessLevel, { label: string; hint: string }> = {
  manage: {
    get label() { return i18n.t('chanCatalog:access.levels.manage.label') },
    get hint() { return i18n.t('chanCatalog:access.levels.manage.hint') },
  },
  agent: {
    get label() { return i18n.t('chanCatalog:access.levels.agent.label') },
    get hint() { return i18n.t('chanCatalog:access.levels.agent.hint') },
  },
}

export const RECEPTION_MODE_META: Record<ReceptionMode, { label: string; hint: string }> = {
  auto: {
    get label() { return i18n.t('chanCatalog:access.modes.auto.label') },
    get hint() { return i18n.t('chanCatalog:access.modes.auto.hint') },
  },
  queue: {
    get label() { return i18n.t('chanCatalog:access.modes.queue.label') },
    get hint() { return i18n.t('chanCatalog:access.modes.queue.hint') },
  },
  manual: {
    get label() { return i18n.t('chanCatalog:access.modes.manual.label') },
    get hint() { return i18n.t('chanCatalog:access.modes.manual.hint') },
  },
}

export function isAccessLevel(v: unknown): v is AccessLevel {
  return v === 'manage' || v === 'agent'
}

export function isReceptionMode(v: unknown): v is ReceptionMode {
  return v === 'auto' || v === 'queue' || v === 'manual'
}

/**
 * Nhân viên này có cần được giao kênh riêng không. Khớp `resolveScope` ở backend (shared/data-scope.ts): phạm vi của
 * vai trò động (`dataScope`) quyết định; chưa gán vai trò động thì chỉ Chủ sở hữu / Quản trị viên xem hết.
 * Quản lý KHÔNG tự thấy mọi kênh — chỉ kênh của mình + cấp dưới.
 */
export function needsExplicitAccess(m: { role: string; dataScope?: string | null }): boolean {
  if (m.dataScope) return m.dataScope !== 'all'
  return m.role !== 'owner' && m.role !== 'admin'
}

/**
 * Cảnh báo cấu hình kênh chưa chạy được — hiện ngay cạnh ô chọn chế độ để không phải đợi hội thoại kẹt mới biết.
 * `access` = số người Quản lý / Tiếp nhận của kênh (chưa tính owner/admin vì họ luôn nhận được).
 */
export function receptionWarning(mode: ReceptionMode, access: { manage: number; agent: number }): string | null {
  if (mode === 'auto' && access.agent + access.manage === 0) {
    return i18n.t('chanCatalog:access.warnAutoNoStaff')
  }
  if (mode === 'queue' && access.agent === 0) {
    return i18n.t('chanCatalog:access.warnQueueNoAgent')
  }
  return null
}

/** Chữ tóm tắt số người trên kênh: "2 quản lý · 3 tiếp nhận". */
export function accessSummary(access: { manage: number; agent: number }): string {
  const parts = [] as string[]
  if (access.manage) parts.push(i18n.t('chanCatalog:access.summaryManage', { count: access.manage }))
  if (access.agent) parts.push(i18n.t('chanCatalog:access.summaryAgent', { count: access.agent }))
  return parts.length ? parts.join(' · ') : i18n.t('chanCatalog:access.summaryNone')
}
